/**
 * Chat API service.
 * Pure async functions — no React state, no hooks.
 */
import client, { get, post, del } from '@core/api/client';
import { endpoints } from '@core/api/endpoints';
import { requestContext } from '@core/api/requestContext';
import { SESSION_KEYS } from '@core/auth/session';
import { env } from '@core/config/env';
import type { PaginatedResponse, PaginationParams } from '@core/api/types';
import { consumeSseBuffer } from './parse-sse';

// ── Domain types ──────────────────────────────────────────────────────────────

export type ConversationStatus = 'active' | 'archived';
export type MessageRole = 'user' | 'assistant' | 'system';

export interface ChatCitation {
  rank: number;
  chunkId: string;
  fileId: string;
  filename?: string;
  excerpt: string;
  score: number;
}

export interface ChatMessage {
  id: string;
  sessionId: string;
  role: MessageRole;
  content: string;
  sequenceNumber: number;
  model?: string;
  citations?: ChatCitation[];
  createdAt?: string;
}

import type {
  SessionOutcomeKind,
  TicketRef,
} from '@features/observability/types';
export interface Conversation {
  id: string;
  agentId: string;
  title?: string;
  status: ConversationStatus;
  messageCount?: number;
  lastMessageAt?: string;
  createdAt?: string;
  updatedAt?: string;
  isReturning?: boolean;
  channel?: string;
  /** Derived outcome + filed tickets (A8). */
  outcome?: SessionOutcomeKind;
  escalated?: boolean;
  tickets?: TicketRef[];
}

export interface CreateConversationPayload {
  title?: string;
  visitorId?: string;
  contactEmail?: string;
}

/** Public chat endpoint — used by both admin and widget */
export interface SendMessagePayload {
  agentId: string;
  sessionId: string;
  message: string;
  /** Widget `acw_visitor_id` for per-visitor usage limits (B4.1). */
  visitorId?: string;
  leadFormData?: Record<string, string>;
  bugFormData?: Record<string, string>;
  handoffFormData?: Record<string, string>;
  scheduleFormData?: Record<string, string>;
  /** Generic submission for any other action tool's form (`toolForm`). */
  toolFormData?: { tool: string; values: Record<string, string> };
  pendingBugForm?: boolean;
  abandonedBugFormData?: Record<string, string>;
}

export type AnswerConfidence = 'high' | 'low' | 'none';
export type MessageFeedbackRating = 'up' | 'down';

export interface SendMessageResponse {
  answer: string;
  messageId: string;
  sources: ChatCitation[];
  timestamp: string;
  confidence?: AnswerConfidence;
  guardrailApplied?: boolean;
  isReturning?: boolean;
  leadForm?: LeadCaptureForm;
  bugForm?: StructuredChatForm;
  handoffForm?: StructuredChatForm;
  scheduleForm?: StructuredChatForm;
  /** Form for any other action tool that asked for details (generic path). */
  toolForm?: ToolChatForm;
  /** Soft warn when approaching a usage limit (B2.3/B2.4). */
  usageWarning?: boolean;
  usageWarningCode?: string | null;
}

export interface LeadCaptureFormField {
  name: string;
  label: string;
  required: boolean;
  type?: 'text' | 'email' | 'textarea' | 'select' | 'image';
  options?: Array<{ value: string; label: string }>;
  optionGroups?: Array<{
    label: string;
    options: Array<{ value: string; label: string }>;
  }>;
  value?: string;
  /**
   * Where a prefilled `value` came from. Shown under the input so the visitor
   * can see and correct it before submitting; `needsConfirmation` marks a
   * low-confidence guess.
   */
  prefill?: FormFieldPrefill;
}

export interface FormFieldPrefill {
  source: 'statement' | 'answer' | 'tool' | 'form' | 'extract';
  confidence: number;
  needsConfirmation: boolean;
}

export interface LeadCaptureForm {
  title: string;
  description?: string;
  submitLabel?: string;
  fields: LeadCaptureFormField[];
  missingFields: string[];
}

export type StructuredChatForm = LeadCaptureForm;

/** `StructuredChatForm` tied to the action tool a submission executes. */
export type ToolChatForm = StructuredChatForm & { tool: string };

export interface SubmitFeedbackPayload {
  agentId: string;
  sessionId: string;
  messageId: string;
  rating: MessageFeedbackRating;
}

// ── SSE stream types (C2.1) ───────────────────────────────────────────────────

export type ChatStreamStatusPhase =
  | 'thinking'
  | 'tool_start'
  | 'reading'
  | 'drafting'
  | 'checking'
  | 'preparing'
  | 'reviewing'
  | 'composing';

export interface ChatStreamStatusData {
  phase: ChatStreamStatusPhase;
  toolName?: string;
  label: string;
}

export interface ChatStreamStepData {
  toolName: string;
  label: string;
  summary?: string;
  ok: boolean;
  latencyMs?: number;
}

export interface ChatStreamDeltaData {
  text: string;
}

export interface ChatStreamErrorData {
  code: string;
  message: string;
}

export interface ChatStreamHandlers {
  onStatus?: (data: ChatStreamStatusData) => void;
  onStep?: (data: ChatStreamStepData) => void;
  onDelta?: (data: ChatStreamDeltaData) => void;
  onDone?: (data: SendMessageResponse) => void;
  onError?: (data: ChatStreamErrorData) => void;
}

function generateRequestId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

/** Same tenant/auth headers as the axios interceptor (SSE uses fetch). */
function buildPublicChatHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };

  const token = localStorage.getItem(SESSION_KEYS.AUTH_TOKEN);
  if (token) headers.Authorization = `Bearer ${token}`;

  const tenantId =
    requestContext.getTenantId() ||
    localStorage.getItem(SESSION_KEYS.TENANT_ID);
  if (tenantId) headers['x-tenant-id'] = tenantId;

  const userId = localStorage.getItem(SESSION_KEYS.USER_ID);
  if (userId) headers['x-user-id'] = userId;

  const agentId = requestContext.getAgentId();
  if (agentId) headers['x-agent-id'] = agentId;

  headers['x-request-id'] = generateRequestId();
  headers['x-client-version'] = env.APP_ENV;

  return headers;
}

function publicChatStreamUrl(): string {
  const base =
    (typeof client.defaults.baseURL === 'string' && client.defaults.baseURL) ||
    `${env.API_BASE_URL}${env.API_PREFIX}`;
  return `${base.replace(/\/$/, '')}${endpoints.chat.messageStream}`;
}

function dispatchStreamEvent(
  eventName: string,
  rawData: string,
  handlers: ChatStreamHandlers,
): void {
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawData) as unknown;
  } catch {
    handlers.onError?.({
      code: 'SSE_PARSE_ERROR',
      message: 'Invalid stream event data.',
    });
    return;
  }

  switch (eventName) {
    case 'status':
      handlers.onStatus?.(parsed as ChatStreamStatusData);
      break;
    case 'step':
      handlers.onStep?.(parsed as ChatStreamStepData);
      break;
    case 'delta':
      handlers.onDelta?.(parsed as ChatStreamDeltaData);
      break;
    case 'done':
      handlers.onDone?.(parsed as SendMessageResponse);
      break;
    case 'error': {
      const err = parsed as Partial<ChatStreamErrorData>;
      handlers.onError?.({
        code: typeof err.code === 'string' ? err.code : 'STREAM_ERROR',
        message:
          typeof err.message === 'string'
            ? err.message
            : 'Failed to process message.',
      });
      break;
    }
    default:
      break;
  }
}

async function readJsonErrorBody(
  response: Response,
): Promise<ChatStreamErrorData> {
  const fallback: ChatStreamErrorData = {
    code: 'HTTP_ERROR',
    message: `Request failed (${response.status})`,
  };
  try {
    const body = (await response.json()) as Record<string, unknown>;
    return {
      code: typeof body.code === 'string' ? body.code : fallback.code,
      message:
        typeof body.message === 'string' ? body.message : fallback.message,
    };
  } catch {
    return fallback;
  }
}

// ── Service ───────────────────────────────────────────────────────────────────

export const chatService = {
  // ── Conversations ──────────────────────────────────────────────────────────

  /**
   * List all conversations for an agent (paginated).
   */
  listConversations(
    agentId: string,
    params?: PaginationParams,
  ): Promise<PaginatedResponse<Conversation>> {
    return get(endpoints.conversations.list(agentId), { params });
  },

  /**
   * Get a single conversation by session ID.
   */
  getConversation(agentId: string, sessionId: string): Promise<Conversation> {
    return get(endpoints.conversations.getById(agentId, sessionId));
  },

  /** Connector-agnostic activity timeline for a conversation (what the agent did). */
  getActivity(
    agentId: string,
    sessionId: string,
  ): Promise<import('@features/observability/types').AgentActionEntry[]> {
    return get(endpoints.activity.forSession(agentId, sessionId));
  },

  /** Full conversation journey in one read: meta + transcript + actions. */
  getConversationJourney(
    agentId: string,
    sessionId: string,
  ): Promise<{
    conversation: Conversation;
    messages: ChatMessage[];
    actions: import('@features/observability/types').AgentActionEntry[];
  }> {
    return get(endpoints.conversations.journey(agentId, sessionId));
  },

  /**
   * Start a new conversation session under an agent (admin / preview — JWT).
   */
  createConversation(
    agentId: string,
    payload?: CreateConversationPayload,
  ): Promise<Conversation> {
    return post(endpoints.conversations.create(agentId), payload ?? {});
  },

  /**
   * Public session create with widget publishable key (B4.2) — no admin JWT.
   */
  createPublicSession(payload: {
    agentId: string;
    publishableKey: string;
    visitorId?: string;
    title?: string;
  }): Promise<Conversation> {
    return post(endpoints.chat.sessions, payload);
  },

  /**
   * Public resume — load transcript for a visitor-owned session (no admin JWT).
   */
  listPublicSessionMessages(payload: {
    agentId: string;
    sessionId: string;
    publishableKey: string;
    visitorId: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<ChatMessage>> {
    const { sessionId, ...params } = payload;
    return get(endpoints.chat.sessionMessages(sessionId), { params });
  },

  /**
   * Delete a conversation and all its messages.
   */
  deleteConversation(agentId: string, sessionId: string): Promise<void> {
    return del(endpoints.conversations.delete(agentId, sessionId));
  },

  // ── Messages ───────────────────────────────────────────────────────────────

  /**
   * Fetch the message history of a conversation (paginated, newest last).
   */
  listMessages(
    agentId: string,
    sessionId: string,
    params?: PaginationParams,
  ): Promise<PaginatedResponse<ChatMessage>> {
    return get(endpoints.messages.list(agentId, sessionId), { params });
  },

  // ── Public chat endpoint ───────────────────────────────────────────────────

  /**
   * Send a user message and receive an AI answer with source citations.
   *
   * This hits the public `/chat/message` endpoint which is also used by
   * the embeddable widget — it does not require the admin Bearer token.
   */
  sendMessage(payload: SendMessagePayload): Promise<SendMessageResponse> {
    return post(endpoints.chat.message, payload);
  },

  /**
   * Stream a chat turn over SSE (`POST /chat/message/stream`).
   * Does not replace {@link sendMessage} — C2.2 wires the widget to this.
   */
  async sendMessageStream(
    payload: SendMessagePayload,
    handlers: ChatStreamHandlers,
    signal?: AbortSignal,
  ): Promise<void> {
    const url = publicChatStreamUrl();

    let response: Response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: buildPublicChatHeaders(),
        body: JSON.stringify(payload),
        signal,
        credentials: 'include',
      });
    } catch (err) {
      if (signal?.aborted) return;
      handlers.onError?.({
        code: 'NETWORK_ERROR',
        message:
          err instanceof Error ? err.message : 'Network request failed.',
      });
      return;
    }

    if (!response.ok) {
      handlers.onError?.(await readJsonErrorBody(response));
      return;
    }

    if (!response.body) {
      handlers.onError?.({
        code: 'STREAM_UNSUPPORTED',
        message: 'Streaming response body is not available.',
      });
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const consumed = consumeSseBuffer(buffer);
        buffer = consumed.rest;

        for (const event of consumed.events) {
          dispatchStreamEvent(event.event, event.data, handlers);
        }
      }

      buffer += decoder.decode();
      if (buffer.trim()) {
        const consumed = consumeSseBuffer(`${buffer}\n\n`);
        for (const event of consumed.events) {
          dispatchStreamEvent(event.event, event.data, handlers);
        }
      }
    } catch (err) {
      if (signal?.aborted) return;
      handlers.onError?.({
        code: 'STREAM_READ_ERROR',
        message:
          err instanceof Error ? err.message : 'Failed while reading stream.',
      });
    }
  },

  /**
   * Record thumbs-up/down on an assistant answer (public widget endpoint).
   */
  submitFeedback(payload: SubmitFeedbackPayload): Promise<void> {
    return post(endpoints.chat.feedback, payload);
  },
} as const;
